import { Expand, MapPin, MapPinned, Plus, Sparkles, Trash2, X } from 'lucide-react';
import { useMemo, useReducer, useRef, useState } from 'react';
import { MAX_ALIASES_INPUT_LENGTH, MAX_ITEM_TEXT_LENGTH } from '../lib/item-values';
import { mergeTerms, normalizeText } from '../lib/semantic';
import type { EditorMode, ItemRecord } from '../types';
import { ItemPhoto } from './ItemPhoto';
import { Modal } from './Modal';

type Props = {
  item: ItemRecord;
  mode: EditorMode;
  knownGroups: string[];
  onClose: () => void;
  onDelete: (item: ItemRecord) => Promise<void>;
  onSave: (item: ItemRecord, values: { name: string; locationNote: string; locationGroup: string; synonyms: string[] }) => Promise<void>;
  onSkip: (item: ItemRecord) => Promise<void>;
  onSuggestGroup: (locationNote: string, existingGroups: string[]) => Promise<string>;
};

export function ItemEditor({ item, mode, knownGroups, onClose, onDelete, onSave, onSkip, onSuggestGroup }: Props) {
  const [form, dispatch] = useReducer(formReducer, item, (initialItem) => ({
    name: initialItem.name,
    locationNote: initialItem.locationNote,
    locationGroup: initialItem.locationGroup || initialItem.locationNote,
    groupFollowsLocation: !initialItem.locationGroup || normalizeText(initialItem.locationGroup) === normalizeText(initialItem.locationNote),
    aliases: initialItem.synonyms.join(', '),
    busy: false,
    suggestingGroup: false,
    error: ''
  }));
  const nameRef = useRef<HTMLInputElement>(null);
  const locationRef = useRef<HTMLInputElement>(null);
  const [photoOpen, setPhotoOpen] = useState(false);
  const requiresLocation = mode === 'location-review';
  const title = mode === 'new' ? 'Name this item' : mode === 'name-review' ? 'Review capture' : mode === 'location-review' ? 'Add location' : 'Edit item';
  const suggestions = useMemo(() => item.aiSuggestions.flatMap((suggestion) => suggestion.trim() ? [suggestion] : []).slice(0, 10), [item.aiSuggestions]);
  const nameSuggestions = item.name.trim() ? [] : suggestions;
  const aliasSuggestions = item.name.trim()
    ? suggestions.filter((suggestion) => normalizeText(suggestion) !== normalizeText(form.name))
    : [];

  const runAction = async (action: () => Promise<void>, fallback: string) => {
    dispatch({ type: 'busy' });
    try {
      await action();
    } catch (error) {
      dispatch({ type: 'error', error: error instanceof Error ? error.message : fallback });
    } finally {
      dispatch({ type: 'idle' });
    }
  };

  const save = () => runAction(
    () => onSave(item, {
        name: form.name,
        locationNote: form.locationNote,
        locationGroup: form.locationGroup,
        synonyms: form.aliases.split(',').flatMap((alias) => alias.trim() ? [alias.trim()] : [])
      }),
    'This item could not be saved.'
  );

  const photoAlt = item.name ? `Photo of ${item.name}` : 'Captured household item';
  const initialFocusRef = requiresLocation ? locationRef : !item.name ? nameRef : undefined;
  const photoLabel = `Full photo of ${item.name || 'captured household item'}`;
  const addAliases = (aliases: string[]) => dispatch({
    type: 'field',
    field: 'aliases',
    value: mergeTerms(form.aliases.split(','), aliases).join(', ')
  });
  const suggestGroup = async () => {
    const requestedLocation = form.locationNote;
    dispatch({ type: 'suggesting-group' });
    try {
      const suggestion = await onSuggestGroup(requestedLocation, knownGroups);
      if (suggestion) dispatch({ type: 'group-suggestion', value: suggestion, requestedLocation });
      else dispatch({ type: 'error', error: 'Gemini could not suggest a section. Keep the exact place or enter one manually.' });
    } catch (error) {
      dispatch({ type: 'error', error: error instanceof Error ? error.message : 'Gemini could not suggest a section.' });
    } finally {
      dispatch({ type: 'suggestion-idle' });
    }
  };

  return (
    <Modal
      className={photoOpen ? 'photo-viewer' : 'item-editor'}
      layerClassName={photoOpen ? 'photo-viewer-layer' : 'item-editor-layer'}
      ariaLabel={photoOpen ? photoLabel : undefined}
      ariaLabelledBy={photoOpen ? undefined : 'editor-title'}
      initialFocusRef={initialFocusRef}
      onClose={photoOpen ? () => setPhotoOpen(false) : onClose}
    >
      {photoOpen ? (
        <PhotoViewer item={item} label={photoLabel} onClose={() => setPhotoOpen(false)} />
      ) : (
        <>
        <header className="editor-header">
          <button className="icon-button quiet" type="button" aria-label="Close item editor" onClick={onClose}><X size={22} /></button>
          <h2 id="editor-title">{title}</h2>
          <button className="icon-button quiet" type="button" aria-label="Open full photo" title="Open full photo" onClick={() => setPhotoOpen(true)}><Expand size={20} aria-hidden="true" /></button>
        </header>

        <div className="editor-content">
          <button className="editor-photo-button" type="button" aria-label="View full photo" onClick={() => setPhotoOpen(true)}>
            <ItemPhoto photo={item.photo} photoMimeType={item.photoMimeType} alt={photoAlt} className="editor-photo" />
            <span><Expand size={15} aria-hidden="true" />View photo</span>
          </button>
          <div className="editor-fields">
            {nameSuggestions.length ? (
              <div className="suggestion-block">
                <span><Sparkles size={16} aria-hidden="true" />Likely matches</span>
                <div className="chips">{nameSuggestions.map((suggestion) => <button type="button" key={suggestion} onClick={() => dispatch({ type: 'field', field: 'name', value: suggestion })}>{suggestion}</button>)}</div>
              </div>
            ) : null}
            <label>
              <span>Item name</span>
              <input ref={nameRef} value={form.name} maxLength={MAX_ITEM_TEXT_LENGTH} onChange={(event) => dispatch({ type: 'field', field: 'name', value: event.target.value })} placeholder="e.g. Spare keys" autoComplete="off" />
            </label>
            <label>
              <span>Where is it kept?</span>
              <div className="input-with-icon"><MapPin size={18} aria-hidden="true" /><input ref={locationRef} value={form.locationNote} maxLength={MAX_ITEM_TEXT_LENGTH} onChange={(event) => dispatch({ type: 'location', value: event.target.value })} placeholder="e.g. First floor Ali room" autoComplete="off" /></div>
            </label>
            {form.locationNote.trim() ? (
              <label>
                <span className="field-heading"><span>Gallery section <small>(controls the group heading)</small></span><button className="field-ai-action" type="button" onClick={() => void suggestGroup()} disabled={form.suggestingGroup}><Sparkles size={14} />{form.suggestingGroup ? 'Thinking…' : 'Ask Gemini'}</button></span>
                <div className="input-with-icon"><MapPinned size={18} aria-hidden="true" /><input list="gallery-section-options" value={form.locationGroup} maxLength={MAX_ITEM_TEXT_LENGTH} onChange={(event) => dispatch({ type: 'group', value: event.target.value })} placeholder={form.locationNote || 'e.g. First floor'} autoComplete="off" /></div>
                <datalist id="gallery-section-options">{knownGroups.map((group) => <option value={group} key={group}>{group}</option>)}</datalist>
                <small className="field-help">Defaults to the exact place. Gemini only changes it when you ask.</small>
              </label>
            ) : null}
            <label>
              <span>Other names <small>(optional, separated by commas)</small></span>
              <input value={form.aliases} maxLength={MAX_ALIASES_INPUT_LENGTH} onChange={(event) => dispatch({ type: 'field', field: 'aliases', value: event.target.value })} placeholder="key, chaabi, house keys" autoComplete="off" />
            </label>
            {aliasSuggestions.length ? (
              <div className="suggestion-block alias-suggestions">
                <span><Sparkles size={16} aria-hidden="true" />Gemini search suggestions<button type="button" className="add-all-suggestions" onClick={() => addAliases(aliasSuggestions)}>Add all</button></span>
                <div className="chips">{aliasSuggestions.map((suggestion) => <button type="button" key={suggestion} onClick={() => addAliases([suggestion])}><Plus size={13} />{suggestion}</button>)}</div>
                <small>These help search automatically. Add only the names you want to keep in “Other names.”</small>
              </div>
            ) : null}
            {form.error ? <p className="form-error" role="alert">{form.error}</p> : null}
          </div>
        </div>

        <footer className="editor-actions">
          <button className="button danger" type="button" onClick={() => void runAction(() => onDelete(item), 'This item could not be deleted.')} disabled={form.busy}><Trash2 size={18} />Delete</button>
          <button className="button secondary" type="button" onClick={() => void (mode === 'name-review' || mode === 'location-review' ? runAction(() => onSkip(item), 'The next review item could not be opened.') : onClose())} disabled={form.busy}>{mode === 'edit' ? 'Cancel' : mode === 'location-review' ? 'Later' : 'Review later'}</button>
          <button className="button primary" type="button" onClick={() => void save()} disabled={form.busy || !form.name.trim() || (requiresLocation && !form.locationNote.trim())}>
            {form.busy ? <span className="spinner" aria-hidden="true" /> : null}Save item
          </button>
        </footer>
        </>
      )}
    </Modal>
  );
}

function PhotoViewer({ item, label, onClose }: { item: ItemRecord; label: string; onClose: () => void }) {
  return (
    <>
      <button className="photo-viewer-close" type="button" aria-label="Close full photo" onClick={onClose}><X size={24} aria-hidden="true" /></button>
      <ItemPhoto photo={item.photo} photoMimeType={item.photoMimeType} alt={label} className="photo-viewer-image" />
    </>
  );
}

type FormState = { name: string; locationNote: string; locationGroup: string; groupFollowsLocation: boolean; aliases: string; busy: boolean; suggestingGroup: boolean; error: string };
type FormAction =
  | { type: 'field'; field: 'name' | 'aliases'; value: string }
  | { type: 'location'; value: string }
  | { type: 'group'; value: string }
  | { type: 'group-suggestion'; value: string; requestedLocation: string }
  | { type: 'suggesting-group' }
  | { type: 'suggestion-idle' }
  | { type: 'busy' }
  | { type: 'idle' }
  | { type: 'error'; error: string };

function formReducer(state: FormState, action: FormAction): FormState {
  if (action.type === 'field') return { ...state, [action.field]: action.value };
  if (action.type === 'location') return { ...state, locationNote: action.value, locationGroup: state.groupFollowsLocation ? action.value : state.locationGroup };
  if (action.type === 'group') return { ...state, locationGroup: action.value, groupFollowsLocation: normalizeText(action.value) === normalizeText(state.locationNote) };
  if (action.type === 'group-suggestion') {
    if (normalizeText(action.requestedLocation) !== normalizeText(state.locationNote)) return state;
    return { ...state, locationGroup: action.value, groupFollowsLocation: false, error: '' };
  }
  if (action.type === 'suggesting-group') return { ...state, suggestingGroup: true, error: '' };
  if (action.type === 'suggestion-idle') return { ...state, suggestingGroup: false };
  if (action.type === 'busy') return { ...state, busy: true, error: '' };
  if (action.type === 'idle') return { ...state, busy: false };
  return { ...state, busy: false, error: action.error };
}
